import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthState } from '@common/components/login/enums';
import { RoutePath } from '@common/enums';
import { UIErrorStatus } from '@common/enums/ui-error-status.enum';
import { ODataError } from '@common/types';
import { getErrorText, isNoLicensesAvailable } from '@common/utils';
import { ToastService } from 'mbs-ui-kit';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export const passwordErrorText = 'Password is not correct';
export const unidentifiedErrorText = 'Sorry, something went wrong';
export const forbiddenErrorText = "Forbidden. You don't have permission to access this";
export const forbiddenBackupErrorText = "Forbidden. You don't have permission to access the backup data";
export const forbiddenExportToPSTErrorText = `Export to\u00A0PST requires Global Admin access.\n
                                              Contact your provider to\u00A0enable Global Admin access for the domain on\u00A0<span class="fw-semibold">M365/Google\u00A0Backup\u00A0>\u00A0Domains</span>`;
export const errorTitle = 'Error Details';

const excludedRoutes = [`/${RoutePath.Contacts}`, `/${RoutePath.Calendar}`];
const assignUiErrorStatus = (error: any, status: UIErrorStatus): void => void ((error.error ??= {}).uiErrorStatus = status);

const getForbiddenMessage = (error: HttpErrorResponse, currentPath: string): string => {
  if (error.url?.endsWith('ExportUserData')) {
    return forbiddenExportToPSTErrorText;
  }

  if (excludedRoutes.some((route) => currentPath.includes(route))) {
    return forbiddenBackupErrorText;
  }

  return forbiddenErrorText;
};

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toastService = inject(ToastService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // skip 401 or rethrow for prevention show toast
      if (error.status === 401 || error.status === 412) {
        return throwError(() => error);
      }

      if (error.status === 403) {
        assignUiErrorStatus(error, UIErrorStatus.Displayed);
        toastService.error(getForbiddenMessage(error, router.url.split(/[?#]/)[0]), errorTitle);

        return throwError(() => error);
      }

      if (error.status === 503) {
        void router.navigate([RoutePath.Account], { queryParams: { state: AuthState.ServiceUnavailable } });

        return throwError(() => error);
      }

      if (error.status === 400 && error.error?.value?.toLowerCase() === passwordErrorText.toLowerCase()) {
        return throwError(() => error);
      }

      const errorResponse = error.error as ODataError | (Blob & Body);

      if (errorResponse instanceof Blob) {
        errorResponse
          .text()
          .then((text) => {
            try {
              const message = getErrorText({ error: JSON.parse(text) } as HttpErrorResponse);
              assignUiErrorStatus(error, UIErrorStatus.Displayed);

              toastService.error(message, errorTitle);
            } catch (e) {
              throw new Error('Cannot parse blob error text in `Error Handler Interceptor`!');
            }
          })
          .catch(() => {
            assignUiErrorStatus(error, UIErrorStatus.Issue);
            toastService.error(unidentifiedErrorText, errorTitle);
          });
      } else if (errorResponse?.value && !isNoLicensesAvailable(error)) {
        const message = errorResponse.value;
        assignUiErrorStatus(error, UIErrorStatus.Displayed);

        toastService.error(message, errorTitle);
      }

      return throwError(() => error);
    })
  );
};
